import { useCallback, useState } from 'react';

import { Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import styles from './TrainingPhrase.module.scss';

interface Props {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  onDelete: () => void;
  readOnly: boolean;
  hasErrors: boolean;
}

/**
 * TrainingPhrase component for individual training phrase editing
 *
 * This component provides an inline editing interface for a single training phrase.
 * It supports:
 * - Real-time text editing with auto-resize
 * - Keyboard shortcuts (Enter to save)
 * - Validation error display
 * - Delete functionality
 * - Read-only mode support
 *
 * @param value - The current value of the training phrase
 * @param onChange - Callback function when the phrase value changes
 * @param onBlur - Callback function when the phrase loses focus
 * @param onDelete - Callback function to delete the phrase
 * @param readOnly - Whether the phrase is in read-only mode
 * @param hasErrors - Whether the phrase has errors
 * @returns JSX element
 */
const TrainingPhrase = ({ value, onChange, onBlur, onDelete, readOnly, hasErrors }: Props) => {
  // ============================================================================
  // HOOKS & EXTERNAL DATA
  // ============================================================================

  const { t } = useTranslation();

  /** Capped to one row at rest; the cap is lifted on click so the whole phrase is readable. */
  const [maxRows, setMaxRows] = useState<number | undefined>(1);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handles key down events for the textarea
   *
   * This function provides keyboard shortcuts for better user experience:
   * - Enter key: Prevents default behavior and triggers blur (saves the phrase)
   * - Other keys: Allow normal text editing behavior
   *
   * @param e - Keyboard event from the textarea
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Save phrase on Enter key press
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <li className={styles.training_phrase}>
      {/* Main Input Container */}
      <div className={styles.input_container}>
        {/* Editable Textarea */}
        <Textarea
          autoSize
          maxRows={maxRows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onClick={() => setMaxRows(undefined)}
          onMouseDown={() => setMaxRows(undefined)}
          onBlur={() => {
            setMaxRows(1);
            onBlur();
          }}
          onKeyDown={handleKeyDown}
          placeholder={t('training_phrases.placeholder')}
          readOnly={readOnly}
          data-testid="e__input"
          className={cn(
            // overflow-hidden clips the capped row instead of showing a scrollbar; pr-9 is the
            // gutter that keeps text clear of the absolutely-positioned delete button.
            'w-full resize-none overflow-hidden rounded-md border bg-background py-2 pr-9 pl-4 text-sm leading-5 text-foreground outline-none placeholder:text-foreground-muted',
            hasErrors ? 'border-stroke-destructive' : 'border-stroke',
            readOnly && 'pointer-events-none bg-bg-muted-50 text-foreground-muted'
          )}
        />

        {/* Delete Button - Only show when not read-only and phrase has content */}
        {!readOnly && !!value && (
          <Button
            className="hover:text-destructive hover:[&_svg]:text-destructive"
            variant="ghost-neutral"
            size="icon-sm"
            aria-label={t('common.delete')}
            onClick={onDelete}
          >
            <Trash2Icon size={16} />
          </Button>
        )}
      </div>

      {/* Error Message Display */}
      {hasErrors && (
        <div className={styles.error_message}>
          <span className="text-sm leading-5 font-normal text-warning">
            {t('training_phrases.error')}
          </span>
        </div>
      )}
    </li>
  );
};

export default TrainingPhrase;
